import { Disclosure } from "@headlessui/react";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import React from "react";

import CrossCountry from "~/types/cross-country";
import accordionUtils from "~/utils/accordion-utils";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import productUtils from "~/utils/product-utils";

import Grid from "../common/grid";
import Icon from "../common/icon";
import styles from "./product-more-info.module.scss";
import ProductServicesIcons from "./product-services-icons";

enum EnvironmentLabelIcons {
  Y = "sorting-container",
  G = "sorting-glass",
}

type Props = {
  selectedShade: CrossCountry.DetailedProduct;
};

export default function ProductMoreInfo(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const country = router.locale?.split("-")[1] || "AE";

  const refAccordions = useRef<HTMLButtonElement[]>([]);

  const environmentLabels = productUtils.getProductEnvironmentLabels(props.selectedShade, {
    customParser: Constants.ENVIRONMENT_LABELS.includes(country.toUpperCase()),
  });
  const enableEnvironmentLabels = CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_ENVIRONMENT_LABELS);

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [scrollTop, setScrollTop] = useState<number>(0);
  const refTabs = useRef<HTMLElement[] | null[] | undefined[]>([]);
  const previousIndexRef = useRef<number | undefined>(undefined);

  // when selected shades changes reset to active index 0
  useEffect(() => {
    if (props.selectedShade) {
      setActiveIndex(0);
    }
  }, [props.selectedShade]);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    function translateAfterSelect() {
      const parentPos = refTabs.current[activeIndex]?.parentElement?.getBoundingClientRect();
      const childPos = refTabs.current[activeIndex]?.getBoundingClientRect();
      if (parentPos && childPos) {
        // padding left & right is 4 px
        const relativeOffset = childPos.left - parentPos.left - 4;

        refTabs.current[activeIndex]?.parentElement?.scrollTo({ left: relativeOffset, behavior: "smooth" });
      }
    }
    if ((previousIndexRef.current == undefined && activeIndex != 0) || previousIndexRef.current != undefined) {
      scrollTimeout = setTimeout(() => {
        translateAfterSelect();
      }, 500);
      previousIndexRef.current = activeIndex;
    }
    return () => clearTimeout(scrollTimeout);
  }, [activeIndex]);

  const editorialTabs = productUtils.getEditorialTabs(
    props.selectedShade,
    environmentLabels && enableEnvironmentLabels ? environmentLabels : undefined
  );

  return (
    <>
      <Grid className={styles.pdpMoreInfo}>
        {props.selectedShade ? (
          <div className={styles.pdpTabs}>
            {editorialTabs.map(({ key, content }, index) => (
              <Disclosure key={key}>
                {({ open }) => (
                  <>
                    <Disclosure.Button
                      className={styles.disclosureButton}
                      ref={(ref) => {
                        if (refAccordions.current && ref) {
                          refAccordions.current[index] = ref;
                        }
                      }}
                      onClick={() => {
                        accordionUtils.handleClosingDisclosures(refAccordions.current[index], refAccordions);
                      }}
                      data-dd-action-name="PDP_ACCORDION_TOGGLE"
                    >
                      <p>{t(`generic.${key}`)}</p>
                      <Icon width={16} height={16} name={open ? "chevron-up" : "chevron-down"} />
                    </Disclosure.Button>
                    <Disclosure.Panel static className={styles.disclosurePanel}>
                      <div className={styles.panelInner}>
                        {key === "ingredient_list" && <p>{t("generic.ingredient_disclaimer")}</p>}
                        {key === Constants.ENVIRONMENT_LABELS_KEY_NAME ? (
                          <EnvironmentLabelContainer environmentLabels={content} />
                        ) : (
                          <div dangerouslySetInnerHTML={{ __html: content }} />
                        )}
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </div>
        ) : null}
      </Grid>
      <div className={styles.servicesIconsContainerMobile}>
        {<ProductServicesIcons selectedShade={props.selectedShade} />}
      </div>
    </>
  );
}

export function EnvironmentLabelContainer({
  environmentLabels,
}: {
  environmentLabels: string | Record<string, string>;
}) {
  const t = useTranslations();

  return (
    <div className={styles.environmentLabelsContainer}>
      {typeof environmentLabels === "string" ? (
        <p dangerouslySetInnerHTML={{ __html: environmentLabels }} />
      ) : (
        Object.keys(environmentLabels).map((envKey) => {
          const hasMappedIcon = !!EnvironmentLabelIcons[envKey as keyof typeof EnvironmentLabelIcons];
          return (
            <div key={envKey} className={hasMappedIcon ? styles.environmentLabel : undefined}>
              {hasMappedIcon ? (
                <Icon
                  name={EnvironmentLabelIcons[envKey as keyof typeof EnvironmentLabelIcons]}
                  width={64}
                  height={64}
                  aria-hidden
                />
              ) : (
                <span>{envKey}: </span>
              )}
              <span dangerouslySetInnerHTML={{ __html: environmentLabels[envKey] }} />
            </div>
          );
        })
      )}
      <p className={styles.disposalInfo}>{t("generic.disposalInfo")}</p>
    </div>
  );
}
